import type { PairEnrichmentSummaryDto } from '@rhcst/shared';
import { formatUsd, formatUsdPrice, formatPercent, relativeAge, absoluteTime } from '../lib/format.js';

/**
 * Full DexScreener market snapshot on the pair detail page.
 *
 * Read-only display context (docs/DECISION_LOG.md ADR-053). It never affects
 * the risk score or trading policy, and this panel says so explicitly so an
 * operator does not mistake a market-data gap for a scoring gap.
 */
export function EnrichmentPanel({
  enrichment,
}: {
  enrichment: PairEnrichmentSummaryDto | null;
}) {
  return (
    <section className="panel">
      <div className="panel-head">
        <h2 className="panel-title">Market data</h2>
        {enrichment !== null && (
          <span className="label label-dim">
            {enrichment.dex_id ?? 'DexScreener'} · {relativeAge(enrichment.fetched_at)} ago
          </span>
        )}
      </div>

      <div className="panel-body stack-2">
        {enrichment === null ? (
          <div className="empty">
            <div className="empty-mark" aria-hidden="true" />
            <p className="empty-title">Not enriched yet</p>
            <p className="empty-body">
              No DexScreener fetch has succeeded for this pair yet. This is read-only
              market context — it never feeds the risk score or the trade policy
              gate, and its absence does not mean anything is wrong with the pair.
            </p>
          </div>
        ) : (
          <>
            {enrichment.stale && (
              <p className="notice" data-tone="alarm" role="status">
                Stale — last refreshed {relativeAge(enrichment.fetched_at)} ago.
              </p>
            )}

            <dl className="facts">
              <div className="fact">
                <dt className="fact-key">Price (USD)</dt>
                <dd className="fact-value">{formatUsdPrice(enrichment.price_usd)}</dd>
              </div>
              <div className="fact">
                <dt className="fact-key">Price (native)</dt>
                <dd className="fact-value">{formatUsdPrice(enrichment.price_native)}</dd>
              </div>
              <div className="fact">
                <dt className="fact-key">Liquidity (DexScreener)</dt>
                <dd className="fact-value">{formatUsd(enrichment.liquidity_usd)}</dd>
              </div>
              <div className="fact">
                <dt className="fact-key">FDV</dt>
                <dd className="fact-value">{formatUsd(enrichment.fdv_usd, 0)}</dd>
              </div>
              <div className="fact">
                <dt className="fact-key">Market cap</dt>
                <dd className="fact-value">{formatUsd(enrichment.market_cap_usd, 0)}</dd>
              </div>
              <div className="fact">
                <dt className="fact-key">24h volume</dt>
                <dd className="fact-value">{formatUsd(enrichment.volume_24h_usd, 0)}</dd>
              </div>
              <div className="fact">
                <dt className="fact-key">24h price change</dt>
                <dd className="fact-value">{formatPercent(enrichment.price_change_24h_pct)}</dd>
              </div>
              <div className="fact">
                <dt className="fact-key">Last updated</dt>
                <dd className="fact-value" title={absoluteTime(enrichment.fetched_at)}>
                  {relativeAge(enrichment.fetched_at)} ago
                  {enrichment.stale ? ' (stale)' : ''}
                </dd>
              </div>
            </dl>

            {enrichment.dexscreener_pair_url !== null && (
              <p className="panel-note">
                <a
                  className="back-link"
                  href={enrichment.dexscreener_pair_url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  View on DexScreener ↗
                </a>
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
